import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CalendarModule } from 'primeng/calendar';
import { MessageService, ConfirmationService } from 'primeng/api';

import { Transaction, TransactionFilters, TransactionService } from '../../services/transaction.service';
import { Contact, ContactService } from '../../services/contact.service';
import { ProjectHistoryService } from '../../services/project-history.service';
import { RingoverService, BulkSmsResponse } from '../../services/ringover.service';
import { AuthService } from '../../../../shared/service/auth.service';
import { UserService, User as UserProfile } from '../../../../shared/service/user.service';
import { STATUT_LEAD_OPTIONS } from '../contacts/contacts.component';
import { BANQUES_LIST } from '../../models/banque';
import { SMS_RELANCE_SIMULATIONS_BODY } from '../../models/sms-templates';

const BANQUE_MAP = new Map(BANQUES_LIST.map(b => [b.value, b.label]));
const AVATAR_COLORS = ['#6366f1','#8b5cf6','#ec4899','#14b8a6','#f59e0b','#10b981','#3b82f6','#ef4444','#f97316','#06b6d4'];
import {
  typeRepriseOptions, deplacementOptions, travailEnHauteurOptions,
  typesPret, typesProjets, origines as origineOptions,
} from '../../models/enum';

export const PHASES_TRANSACTION = [
  'Nouveau LEAD / RDV',
  'Echange',
  'Devis envoyé',
  'Relance & objections en cours',
  'Questionnaire médical envoyé',
  'Fermé gagné',
  'Fermé perdu',
  'EVENEMENT / AVIS / PARRAINAGE',
];

export const STATUT_CONTRAT_OPTIONS = ['En cours', 'Résilié'];

/** Statuts de lead qui font bouger la phase des transactions ouvertes du contact. */
const PHASE_MOVING_LEAD_STATUSES = [
  'A recontacter', 'Non qualifié - Refus', 'Perdu - Trop Cher', 'Perdu - Refus Assureur',
];

/** Combinaisons Phase du pipe → statuts lead possibles (référentiel « Relation
 *  Phase Statut » : la phase prime sur le statut du lead). Phase absente de la
 *  table (ou transaction sans phase) : tous les statuts restent proposés.
 *  NRP absent de « Devis envoyé » / « Relance » : une fois « En cours - Devis
 *  Envoyé », le statut ne redevient jamais NRP (le rappel manqué se lit dans
 *  resultat_appel) ; un lead Fournisseur/Web resté NRP garde sa valeur courante
 *  (toujours listée par leadStatusOptionsFor). */
export const PHASE_STATUT_LEAD_MAP: Record<string, string[]> = {
  'Nouveau LEAD / RDV':             ['Nouveau', 'NRP'],
  'Echange':                        ['A recontacter', 'NRP'],
  'Devis envoyé':                   ['En cours - Devis Envoyé'],
  'Relance & objections en cours':  ['En cours - Devis Envoyé'],
  'Questionnaire médical envoyé':   ['En cours - QS Envoyé'],
  'Fermé gagné':                    ['Converti en transaction'],
  'Fermé perdu':                    ['Perdu - Trop Cher', 'Perdu - Refus Assureur', 'Non qualifié - Refus'],
};

@Component({
  selector: 'app-transactions',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    TableModule, ButtonModule, InputTextModule,
    DropdownModule, DialogModule, TagModule,
    ToastModule, ProgressSpinnerModule, TooltipModule, ConfirmDialogModule,
    CalendarModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './transactions.component.html',
  styleUrls: ['./transactions.component.scss'],
})
export class TransactionsComponent implements OnInit {
  @ViewChild('csvImportInput') csvImportInput?: ElementRef<HTMLInputElement>;
  @ViewChild('mobileDeleteExcelInput') mobileDeleteExcelInput?: ElementRef<HTMLInputElement>;

  private readonly filtersKey = 'transactionsFiltersState';
  transactions: Transaction[] = [];
  totalCount   = 0;
  loading      = true;
  /** Pagination SERVEUR : `first` = index de la 1re ligne de la page courante,
   *  `pageSize` = lignes par page. La base compte des dizaines de milliers de
   *  transactions, on ne charge donc jamais que la page demandée. */
  first    = 0;
  pageSize = 15;
  private searchTimer: any = null;
  openingForm  = false;
  importingCsv = false;
  deletingByExcel = false;

  // ---------- filtres ----------
  contactId     : number | null  = null;
  selectedPhase : string | null  = null;
  search        = '';
  showFilters   = true;
  /** Verrou sur un projet précis (ouverture depuis l'historique) : n'affiche
   *  que la (les) transaction(s) rattachée(s) à ce project_id exact. */
  lockedProjectId : string | null = null;

  // filtres avancés
  selectedOrigine : string | null = null;
  selectedUserId  : number | null = null;
  dateRange       : Date[] | null = null;
  origineFilterOptions = origineOptions;
  usersFilter     : { label: string; value: number }[] = [];
  isAdmin         = false;
  currentUserId   : number | null = null;

  phaseFilterOptions = [
    { label: 'Toutes les phases', value: null as string | null },
    ...PHASES_TRANSACTION.map(p => ({ label: p, value: p })),
  ];

  // ---------- actions de masse ----------
  selectedAction       : string | null = null;
  selectedAssignUserId : number | null = null;
  exportingExcel       = false;
  sendingBulkInfo      = false;
  sendingBulkSavings   = false;
  sendingBulkSms       = false;
  assigningBulk        = false;
  duplicatingId        : number | null = null;
  private agentProfile : UserProfile | null = null;

  // ---------- SMS relecture (dialog avant envoi) ----------
  smsDialogVisible     = false;
  smsDialogContact     : Contact | null = null;
  smsDialogPhone       = '';
  smsDialogContent     = '';
  smsDialogSending     = false;

  // ---------- statut du lead ----------
  // Référentiel unique de valeurs, partagé avec la fiche contact, l'historique
  // projets et l'agenda. Le statut est porté par LE DOSSIER (Transaction.status) :
  // deux prêts d'un même client peuvent avoir deux statuts différents, la fiche
  // contact affichant l'agrégat (le dossier le plus avancé).
  leadStatusOptions = STATUT_LEAD_OPTIONS;
  savingLeadStatusId: number | null = null;

  // ---------- commentaire (édition) ----------
  commentDialogVisible   = false;
  commentDialogTitle     = '';
  commentDialogText      = '';
  commentDialogProjectId : string | null = null;
  savingComment          = false;

  // ---------- édition inline ----------
  phaseEditOptions          = PHASES_TRANSACTION;
  statutContratOptions      = STATUT_CONTRAT_OPTIONS;
  banquesOptions            = BANQUES_LIST;
  typeRepriseOptions        = typeRepriseOptions;
  deplacementOptions        = deplacementOptions;
  travailEnHauteurOptions   = travailEnHauteurOptions;
  typesPretOptions          = typesPret;
  objetPretOptions          = typesProjets;
  resultatAppelOptions      = ['OK', 'NRP'];
  ouiNonOptions             = [{ label: 'Non', value: false }, { label: 'Oui', value: true }];

  // prêts additionnels 2-5
  pret2: any = {};
  pret3: any = {};
  pret4: any = {};
  pret5: any = {};

  // ---------- sélection groupée ----------
  selected : Transaction[] = [];
  bulkPhase: string | null = null;

  // ---------- détails (transaction + fiche contact) ----------
  detailVisible       = false;
  selectedTransaction : Transaction | null = null;
  selectedContact     : Contact | null = null;
  loadingContact      = false;

  // ---------- création ----------
  createVisible        = false;
  saving               = false;
  newTransaction       : Partial<Transaction> = {};
  contactOptions       : { label: string; value: number }[] = [];
  loadingContactOpts   = false;

  constructor(
    private transactionService: TransactionService,
    private contactService: ContactService,
    private projectHistoryService: ProjectHistoryService,
    private ringoverService: RingoverService,
    private authService: AuthService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    const raw = this.route.snapshot.queryParamMap.get('contactId');
    this.contactId = raw ? Number(raw) : null;
    const projectIdParam = (this.route.snapshot.queryParamMap.get('projectId') || '').trim();
    this.lockedProjectId = projectIdParam || null;
    this.isAdmin = this.authService.isAdmin();
    this.currentUserId = this.getCurrentUserIdFromToken();
    if (!this.contactId && !this.lockedProjectId) this.restoreFilters();
    if (this.isAdmin) this.loadUsers();
    // Commentaire modifié depuis une autre table (historique, agenda).
    this.transactionService.commentaireChanged$.subscribe(
      ({ project_id, commentaire }) => this.applyCommentaireChange(project_id, commentaire),
    );
    // Pas d'appel à load() ici : la table en mode lazy émet onLazyLoad au
    // démarrage, ce qui déclenche le premier chargement avec la bonne page.
  }

  private restoreFilters(): void {
    try {
      const rawState = sessionStorage.getItem(this.filtersKey);
      if (!rawState) return;
      const s = JSON.parse(rawState);
      this.search          = s.search || '';
      this.selectedPhase   = s.selectedPhase ?? null;
      this.selectedOrigine = s.selectedOrigine ?? null;
      this.selectedUserId  = s.selectedUserId ?? null;
      if (Array.isArray(s.dateRange) && s.dateRange.length === 2 && s.dateRange[0] && s.dateRange[1]) {
        this.dateRange = [new Date(s.dateRange[0]), new Date(s.dateRange[1])];
      }
    } catch { sessionStorage.removeItem(this.filtersKey); }
  }

  private persistFilters(): void {
    sessionStorage.setItem(this.filtersKey, JSON.stringify({
      search: this.search,
      selectedPhase: this.selectedPhase,
      selectedOrigine: this.selectedOrigine,
      selectedUserId: this.selectedUserId,
      dateRange: this.dateRange && this.dateRange.length === 2
        ? [this.dateRange[0]?.toISOString(), this.dateRange[1]?.toISOString()] : null,
    }));
  }

  private getCurrentUserIdFromToken(): number | null {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      const uid = Number(decoded?.user_id);
      return Number.isFinite(uid) && uid > 0 ? uid : null;
    } catch { return null; }
  }

  private loadUsers(): void {
    this.userService.getUsers().subscribe({
      next: (users) => {
        this.usersFilter = (users || [])
          .filter(u => u.is_active !== false)
          .map(u => ({ label: u.username || u.email, value: u.id }));
      },
      error: () => {},
    });
  }

  // ===== création =====

  openCreate(): void {
    this.newTransaction = {};
    this.pret2 = {}; this.pret3 = {}; this.pret4 = {}; this.pret5 = {};
    this.createVisible  = true;
    if (!this.contactOptions.length) {
      this.loadingContactOpts = true;
      this.contactService.list(500, 0).subscribe({
        next: (contacts) => {
          this.contactOptions = (contacts || []).map(c => ({
            label : [[c.prenom, c.nom].filter(Boolean).join(' '), c.mobile].filter(Boolean).join(' · ') + ` (#${c.id})`,
            value : c.id,
          }));
          this.loadingContactOpts = false;
        },
        error: () => { this.loadingContactOpts = false; },
      });
    }
  }

  saveTransaction(): void {
    if (!this.newTransaction.contact_id) {
      this.messageService.add({ severity: 'warn', summary: 'Requis', detail: 'Sélectionnez un contact.' });
      return;
    }
    const prets = [this.pret2, this.pret3, this.pret4, this.pret5]
      .filter(p => p.montant_credit || p.type_pret);
    if (prets.length) this.newTransaction.prets_additionnels = prets;
    this.saving = true;
    this.transactionService.create(this.newTransaction).subscribe({
      next: (t) => {
        this.transactions.unshift(t);
        this.saving = false;
        this.createVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Transaction créée', detail: t.reference || t.project_id || '' });
      },
      error: (err) => {
        this.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Impossible de créer la transaction.' });
        console.error(err);
      },
    });
  }

  // ===== données =====

  /** Filtres envoyés au SERVEUR. Tout doit passer par là : la base compte des
   *  dizaines de milliers de transactions, un filtrage en mémoire ne verrait que
   *  la page chargée et laisserait invisible tout ce qui est plus ancien. */
  private buildServerFilters(): TransactionFilters {
    const [start, end] = this.dateRange && this.dateRange.length === 2 ? this.dateRange : [null, null];
    const filters: TransactionFilters = {
      contact_id       : this.contactId       ?? undefined,
      project_id       : this.lockedProjectId ?? undefined,
      phase_transaction: this.selectedPhase   ?? undefined,
      search           : this.search.trim()    || undefined,
      origine          : this.selectedOrigine ?? undefined,
      user_id          : this.selectedUserId  ?? undefined,
    };
    if (start && end) {
      const s = new Date(start); s.setHours(0, 0, 0, 0);
      const e = new Date(end);   e.setHours(23, 59, 59, 999);
      filters.start_date = s.toISOString();
      filters.end_date   = e.toISOString();
    }
    return filters;
  }

  /** Recharge depuis la 1re page : à utiliser dès qu'un FILTRE change, sinon on
   *  resterait sur une page qui n'existe plus dans le nouveau résultat. */
  reload(): void {
    this.first = 0;
    this.load();
  }

  /** Changement de page / de taille de page demandé par le tableau. */
  onLazyLoad(event: TableLazyLoadEvent): void {
    this.first    = event.first ?? 0;
    this.pageSize = event.rows  ?? this.pageSize;
    this.load();
  }

  load(): void {
    this.loading = true;
    if (!this.contactId) this.persistFilters();
    const filters = this.buildServerFilters();
    this.transactionService.count(filters).subscribe({
      next : (r) => this.totalCount = r.count,
      error: () => {},
    });
    this.transactionService.list(this.pageSize, this.first, filters).subscribe({
      next: (data) => {
        this.transactions = data || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Impossible de charger les transactions' });
        console.error(err);
      },
    });
  }

  /** Recherche serveur, temporisée : on ne déclenche pas une requête par touche. */
  onSearchChange(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.reload(), 350);
  }

  /** Plage de dates : on ne recharge qu'une fois les deux bornes choisies (ou
   *  la plage effacée), sinon la première sélection filtrerait sur un seul jour. */
  onDateRangeChange(): void {
    const range = this.dateRange;
    if (!range || !range.length) { this.reload(); return; }
    if (range.length === 2 && range[0] && range[1]) this.reload();
  }

  clearContactFilter(): void {
    this.contactId = null;
    this.router.navigate(['/cac/transactions']);
    this.reload();
  }

  // ===== filtres côté client =====

  get hasActiveFilters(): boolean {
    return !!(this.search.trim() || this.selectedPhase || this.contactId ||
      this.selectedOrigine || this.selectedUserId != null || this.lockedProjectId ||
      (this.dateRange && this.dateRange.length === 2));
  }

  clearFilters(): void {
    this.search          = '';
    this.selectedPhase   = null;
    this.contactId       = null;
    this.selectedOrigine = null;
    this.selectedUserId  = null;
    this.dateRange       = null;
    this.lockedProjectId = null;
    this.router.navigate(['/cac/transactions']);
    this.reload();
  }

  // ===== actions ligne =====

  openDetails(t: Transaction): void {
    this.selectedTransaction = t;
    this.selectedContact     = null;
    this.detailVisible       = true;
    if (t.contact_id != null) {
      this.loadingContact = true;
      this.contactService.get(t.contact_id).subscribe({
        next: (c) => { this.selectedContact = c; this.loadingContact = false; },
        error: (err) => {
          this.loadingContact = false;
          this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Impossible de charger la fiche contact.' });
          console.error(err);
        },
      });
    }
  }

  /** Ouvre le commentaire de la transaction (même source que l'historique et l'agenda). */
  openComment(t: Transaction): void {
    this.commentDialogTitle     = `${this.contactName(t)} · ${t.reference || t.project_id || ''}`;
    this.commentDialogText      = (t.commentaire || '').trim();
    this.commentDialogProjectId = t.project_id || null;
    this.commentDialogVisible   = true;
  }

  /** Enregistre le commentaire : l'historique et l'agenda lisent la même valeur. */
  saveComment(): void {
    const projectId = this.commentDialogProjectId;
    if (!projectId || this.savingComment) return;
    const value = this.commentDialogText.trim();
    this.savingComment = true;
    this.transactionService.updateCommentaireByProject(projectId, value).subscribe({
      next: () => {
        this.savingComment = false;
        this.commentDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Commentaire enregistré', life: 2000 });
      },
      error: (err) => {
        this.savingComment = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Enregistrement impossible.' });
        console.error(err);
      },
    });
  }

  /** Aligne les lignes affichées sur un commentaire modifié ailleurs. */
  private applyCommentaireChange(projectId: string, commentaire: string | null): void {
    this.transactions
      .filter(t => t.project_id === projectId)
      .forEach(t => (t.commentaire = commentaire || undefined));
  }

  fullName(c: Contact): string {
    return [c.prenom, c.nom].filter(Boolean).join(' ') || '—';
  }

  initials(c: Contact): string {
    const f = (c.prenom || '').charAt(0);
    const l = (c.nom || '').charAt(0);
    return (f + l).toUpperCase() || '?';
  }

  callContact(c: Contact): void {
    const number = (c.mobile || c.fixe || '').replace(/\s/g, '');
    if (!number) {
      this.messageService.add({ severity: 'warn', summary: 'Pas de numéro', detail: "Ce contact n'a pas de numéro de téléphone." });
      return;
    }
    // Suivi serveur D'ABORD → Résultat appel (OK/NRP) sur la transaction ;
    // composition ensuite. `location.href = tel:` déclenche une navigation qui
    // peut annuler une requête encore en vol — surtout sur mobile.
    const tx = this.selectedTransaction;
    this.ringoverService.trackCall({
      to_number: number,
      contact_id: c.id,
      transaction_id: tx?.id,
    }).subscribe({
      next: (res) => {
        if (res?.calls_read) {
          this.scheduleResultatAppelRefresh(tx?.id ?? null);
        } else {
          this.messageService.add({
            severity: 'warn', summary: 'Suivi appel désactivé',
            detail: res?.message || "La clé Ringover n'a pas la permission 'Calls Read'.",
            life: 8000,
          });
        }
      },
      error: () => {},
    });
    // Ouvre Ringover avec le numéro (protocole tel:).
    window.location.href = `tel:${number}`;
  }

  /** Rafraîchit le champ Résultat appel de la transaction pendant la fenêtre de
   *  suivi serveur (1 h) : sondes rapprochées pour l'appel non décroché, puis
   *  plus tardives — un appel n'entre dans /calls qu'une fois TERMINÉ. */
  private scheduleResultatAppelRefresh(transactionId: number | null): void {
    if (transactionId == null) return;
    const refresh = () => {
      this.transactionService.get(transactionId).subscribe({
        next: (fresh) => {
          const row = this.transactions.find(x => x.id === transactionId);
          if (row) row.resultat_appel = fresh.resultat_appel;
          const sel = this.selectedTransaction;
          if (sel && sel.id === transactionId) sel.resultat_appel = fresh.resultat_appel;
        },
        error: () => {},
      });
    };
    setTimeout(refresh, 120000);
    setTimeout(refresh, 300000);
    setTimeout(refresh, 660000);
    setTimeout(refresh, 1320000);
  }

  /** Construit le message SMS "Rappel Devis" pré-rédigé pour un contact. */
  private buildSmsRappel(c: Contact, sig: string): string {
    const nom = c.nom || '';
    const civ = c.civilite || '';
    const greeting = ['mme', 'madame', 'mlle', 'mademoiselle'].includes(civ.trim().toLowerCase().replace(/\./g, '')) ? 'Chère' : 'Cher';
    const smsCivilite = this.formatCiviliteForSms(civ);
    const salutation = `${greeting}${smsCivilite ? ' ' + smsCivilite : ''}${nom ? ' ' + nom : ''}`;
    return `${salutation},\n\n${SMS_RELANCE_SIMULATIONS_BODY}\n\nBien à vous\n\n${sig}`;
  }

  /** Ouvre le dialog SMS pré-rédigé pour relecture avant envoi (via API Ringover). */
  smsContact(c: Contact): void {
    const phone = (c.mobile || '').replace(/\s/g, '');
    if (!phone) {
      this.messageService.add({ severity: 'warn', summary: 'Pas de numéro', detail: "Ce contact n'a pas de mobile renseigné." });
      return;
    }
    const openDialog = (agent: UserProfile) => {
      this.smsDialogContact = c;
      this.smsDialogPhone   = phone;
      this.smsDialogContent = this.buildSmsRappel(c, this.buildSignature(agent));
      this.smsDialogVisible = true;
    };
    if (this.agentProfile) openDialog(this.agentProfile);
    else this.userService.getMyProfile().subscribe({
      next: (p) => { this.agentProfile = p; openDialog(p); },
      error: () => openDialog({ id: 0, username: '', email: '', is_active: true, role: 'USER', createdAt: null } as any),
    });
  }

  /** Envoi effectif du SMS depuis le dialog (après relecture). */
  sendSmsFromDialog(): void {
    if (!this.smsDialogContact || !this.smsDialogPhone || !this.smsDialogContent.trim()) return;
    this.smsDialogSending = true;
    this.ringoverService.sendSms({
      to_number: this.smsDialogPhone,
      content: this.smsDialogContent,
      // Gabarit + contact : sans eux, le serveur ne peut pas retrouver le
      // template WhatsApp équivalent et un échec SMS resterait un échec.
      template: 'relance_simulations',
      contact_id: this.smsDialogContact.id!,
    }).subscribe({
      next: (res) => {
        this.smsDialogSending = false;
        this.smsDialogVisible = false;
        // Une bascule mérite d'être signalée : le client a reçu le template
        // WhatsApp approuvé, pas le texte éventuellement retouché ici.
        const viaWhatsapp = res?.channel === 'whatsapp';
        this.messageService.add({
          severity: viaWhatsapp ? 'warn' : 'success',
          summary: viaWhatsapp ? 'Envoyé via WhatsApp' : 'SMS envoyé',
          detail: viaWhatsapp
            ? `À ${this.smsDialogPhone} — SMS impossible (${res.fallback_from_sms_error ?? 'erreur Ringover'}), message envoyé via le template WhatsApp.`
            : `À ${this.smsDialogPhone}`,
          life: viaWhatsapp ? 9000 : 4000,
        });
      },
      error: (err) => {
        this.smsDialogSending = false;
        this.messageService.add({ severity: 'error', summary: 'Échec SMS', detail: err?.error?.detail || 'Impossible d\'envoyer le SMS via Ringover.' });
        console.error(err);
      },
    });
  }

  openInForm(t: Transaction): void {
    // Chargement direct depuis la Transaction (sans dépendre de project_history).
    this.openingForm = true;
    this.transactionService.getFormPayload(t.id).subscribe({
      next: (details) => {
        localStorage.setItem('projectToLoad', JSON.stringify(details));
        this.openingForm = false;
        this.router.navigate(['/cac/comparaison'], { queryParams: { loadProject: details.project_id || t.project_id } });
      },
      error: (err) => {
        this.openingForm = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: "Impossible d'ouvrir le formulaire." });
        console.error(err);
      },
    });
  }

  deleteOne(t: Transaction): void {
    this.confirmationService.confirm({
      message: `Supprimer la transaction ${t.reference || t.project_id} ?`,
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.transactionService.delete(t.id).subscribe({
          next: () => {
            this.transactions = this.transactions.filter(x => x.id !== t.id);
            this.selected     = this.selected.filter(x => x.id !== t.id);
            this.messageService.add({ severity: 'success', summary: 'Supprimée', detail: t.reference || '' });
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Suppression impossible' });
            console.error(err);
          },
        });
      },
    });
  }

  deleteSelected(): void {
    if (!this.selected.length) return;
    this.confirmationService.confirm({
      message: `Supprimer ${this.selected.length} transaction(s) ?`,
      header: 'Confirmation de suppression groupée',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        forkJoin(this.selected.map(t => this.transactionService.delete(t.id))).subscribe({
          next: () => {
            const ids = new Set(this.selected.map(t => t.id));
            this.transactions = this.transactions.filter(x => !ids.has(x.id));
            this.selected     = [];
            this.messageService.add({ severity: 'success', summary: 'Supprimées', detail: `${ids.size} transaction(s)` });
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Suppression groupée impossible' });
            console.error(err);
            this.load();
          },
        });
      },
    });
  }

  applyBulkPhase(): void {
    if (!this.bulkPhase || !this.selected.length) return;
    const phase = this.bulkPhase;
    forkJoin(this.selected.map(t => this.transactionService.update(t.id, { phase_transaction: phase }))).subscribe({
      next: () => {
        this.selected.forEach(t => (t.phase_transaction = phase));
        this.messageService.add({ severity: 'success', summary: 'Phase mise à jour', detail: `${this.selected.length} transaction(s)` });
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Changement de phase groupé impossible' });
        console.error(err);
        this.load();
      },
    });
  }

  updateField(t: Transaction, field: keyof Transaction, value: any): void {
    this.transactionService.update(t.id, { [field]: value } as Partial<Transaction>).subscribe({
      next: (fresh) => {
        (t as any)[field] = value;
        // Le serveur peut faire évoluer la phase automatiquement (ex: devis
        // envoyé → « Devis envoyé »). On reflète la phase renvoyée dans la ligne.
        if (fresh?.phase_transaction) {
          t.phase_transaction = fresh.phase_transaction;
        }
        // ...ainsi que le statut du lead DE CE DOSSIER, que la phase peut poser
        // (ex: « Fermé gagné » → « Converti en transaction »). Les autres
        // dossiers du client ne sont pas concernés.
        if (fresh?.status) {
          t.status = fresh.status;
        }
        this.messageService.add({ severity: 'success', summary: 'Mis à jour', detail: `${field} modifié`, life: 1500 });
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Mise à jour impossible' });
        console.error(err);
        this.load();
      },
    });
  }

  /** Statuts lead proposés pour une transaction : limités aux combinaisons
   *  valides de sa phase (la valeur courante reste listée pour ne pas vider
   *  l'affichage). */
  leadStatusOptionsFor(t: Transaction): string[] {
    const allowed = t.phase_transaction ? PHASE_STATUT_LEAD_MAP[t.phase_transaction] : null;
    if (!allowed) return this.leadStatusOptions;
    const current = t.status || null;
    return this.leadStatusOptions.filter(s => allowed.includes(s) || s === current);
  }

  /** Résultat d'appel (Transaction.resultat_appel) : OK / NRP, null = « Nouveau ». */
  resultatAppelLabel(resultat?: string | null): string {
    return (resultat || '').trim() || 'Nouveau';
  }

  resultatAppelSeverity(resultat?: string | null): 'success' | 'danger' | 'secondary' {
    switch ((resultat || '').trim().toUpperCase()) {
      case 'OK':  return 'success';
      case 'NRP': return 'danger';
      default:    return 'secondary';
    }
  }

  /** Change le statut du lead DE CE DOSSIER. Les autres dossiers du même client
   *  ne sont pas touchés : le serveur applique le statut à la seule transaction
   *  envoyée, puis réaligne la fiche contact sur le dossier le plus avancé. */
  updateLeadStatus(t: Transaction, value: string | null): void {
    if (!value || value === t.status) return;
    const previous = t.status;
    this.savingLeadStatusId = t.id;
    t.status = value;
    this.transactionService.update(t.id, { status: value } as Partial<Transaction>).subscribe({
      next: (fresh) => {
        this.savingLeadStatusId = null;
        t.status = fresh?.status ?? value;
        // Un statut de perte bascule la phase côté serveur.
        if (fresh?.phase_transaction) t.phase_transaction = fresh.phase_transaction;
        this.messageService.add({ severity: 'success', summary: 'Statut du lead', detail: value, life: 2000 });
        // Ces statuts déplacent la phase du dossier : on recharge pour refléter
        // les effets de bord côté serveur (agenda, relances annulées).
        if (PHASE_MOVING_LEAD_STATUSES.includes(value)) this.load();
      },
      error: (err) => {
        this.savingLeadStatusId = null;
        t.status = previous;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Changement de statut impossible' });
        console.error(err);
      },
    });
  }

  // ===== Duplication =====

  duplicateOne(t: Transaction): void {
    if (!t.project_id) {
      this.messageService.add({ severity: 'warn', summary: 'Indisponible', detail: 'Aucun dossier CAC lié.' });
      return;
    }
    this.confirmationService.confirm({
      message: `Dupliquer la transaction ${t.reference || t.project_id} ?`,
      header: 'Confirmer la duplication',
      icon: 'pi pi-copy',
      acceptLabel: 'Dupliquer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.duplicatingId = t.id;
        this.projectHistoryService.duplicateProject(t.project_id).subscribe({
          next: (res) => {
            this.duplicatingId = null;
            this.messageService.add({ severity: 'success', summary: 'Dupliquée', detail: `Nouveau dossier : ${res.new_project_id}` });
            this.load();
          },
          error: (err) => {
            this.duplicatingId = null;
            this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Duplication impossible.' });
            console.error(err);
          },
        });
      },
    });
  }

  // ===== Menu d'actions unifié (global + sélection) =====

  get actionOptions(): { label: string; value: string; icon: string; needsSelection: boolean }[] {
    const opts: { label: string; value: string; icon: string; needsSelection: boolean; admin?: boolean }[] = [
      { label: 'Exporter en Excel',            value: 'export',        icon: 'pi pi-file-excel',  needsSelection: false },
      { label: 'Importer (CSV OGGO)',          value: 'import_csv',    icon: 'pi pi-file-import', needsSelection: false, admin: true },
      { label: 'Supprimer (Excel)',            value: 'delete_excel',  icon: 'pi pi-trash',       needsSelection: false, admin: true },
      { label: 'Changer la phase',             value: 'phase',         icon: 'pi pi-flag',        needsSelection: true },
      { label: 'Email demande d\'information', value: 'email_info',    icon: 'pi pi-envelope',    needsSelection: true },
      { label: 'Email économies',              value: 'email_savings', icon: 'pi pi-percentage',  needsSelection: true },
      { label: 'SMS rappel devis',             value: 'sms_rappel',    icon: 'pi pi-comment',     needsSelection: true },
      { label: 'Attribuer à un conseiller',    value: 'assign',        icon: 'pi pi-users',       needsSelection: true, admin: true },
      { label: 'Supprimer la sélection',       value: 'delete',        icon: 'pi pi-trash',       needsSelection: true },
    ];
    return opts.filter(o => !o.admin || this.isAdmin);
  }

  actionNeedsSelection(value: string | null): boolean {
    return ['phase', 'email_info', 'email_savings', 'sms_rappel', 'assign', 'delete'].includes(value || '');
  }

  canExecuteAction(): boolean {
    if (!this.selectedAction || this.isBulkBusy()) return false;
    if (this.actionNeedsSelection(this.selectedAction) && !this.selected.length) return false;
    if (this.selectedAction === 'phase'  && !this.bulkPhase) return false;
    if (this.selectedAction === 'assign' && !this.selectedAssignUserId) return false;
    return true;
  }

  executeAction(): void {
    switch (this.selectedAction) {
      case 'export':        this.exportExcel(); break;
      case 'import_csv':    this.triggerCsvImport(); break;
      case 'delete_excel':  this.triggerMobileDeleteExcel(); break;
      case 'phase':         this.applyBulkPhase(); break;
      case 'email_info':    this.sendBulkInfoEmail(); break;
      case 'email_savings': this.sendBulkSavingsEmail(); break;
      case 'sms_rappel':    this.sendBulkSmsRappel(); break;
      case 'assign':        this.assignBulk(); break;
      case 'delete':        this.deleteSelected(); break;
    }
  }

  // ===== Export Excel =====

  exportExcel(): void {
    if (this.exportingExcel) return;
    const ids = this.selected.map(t => t.project_id).filter(Boolean) as string[];
    if (ids.length) {
      this.runExcelExport(ids);
      return;
    }
    // Sans sélection : on exporte TOUT le résultat filtré, pas la page affichée
    // (la liste est paginée côté serveur, le navigateur n'en connaît que 15).
    this.exportingExcel = true;
    this.transactionService.projectIds(this.buildServerFilters()).subscribe({
      next: (r) => {
        this.exportingExcel = false;
        this.runExcelExport(r.project_ids || []);
      },
      error: (err) => {
        this.exportingExcel = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Export Excel impossible.' });
        console.error(err);
      },
    });
  }

  private runExcelExport(exportIds: string[]): void {
    if (!exportIds.length) {
      this.messageService.add({ severity: 'warn', summary: 'Export', detail: 'Aucune transaction à exporter.' });
      return;
    }
    this.exportingExcel = true;
    this.projectHistoryService.exportProjectHistoryExcel(exportIds, {}).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) { this.exportingExcel = false; return; }
        const disposition = response.headers.get('content-disposition');
        const match = disposition ? /filename="?([^"]+)"?/.exec(disposition) : null;
        const filename = match?.[1] || `transactions_${new Date().toISOString().slice(0, 10)}.xlsx`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.download = filename;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.messageService.add({ severity: 'success', summary: 'Export terminé', detail: `${exportIds.length} transaction(s) exportée(s).` });
        this.exportingExcel = false;
      },
      error: (err) => {
        this.exportingExcel = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Export Excel impossible.' });
        console.error(err);
      },
    });
  }

  // ===== Emails groupés (réutilise ProjectHistory via project_id) =====

  private bulkProjectIds(): string[] {
    return this.selected.map(t => t.project_id).filter(Boolean);
  }

  sendBulkInfoEmail(): void {
    const ids = this.bulkProjectIds();
    if (!ids.length) { this.messageService.add({ severity: 'warn', summary: 'Sélection vide', detail: 'Aucune transaction sélectionnée.' }); return; }
    this.confirmationService.confirm({
      message: `Envoyer l'email de demande d'information pour ${ids.length} transaction(s) ?`,
      header: 'Confirmer l\'envoi groupé', icon: 'pi pi-envelope',
      acceptLabel: 'Envoyer', rejectLabel: 'Annuler',
      accept: () => {
        this.sendingBulkInfo = true;
        this.projectHistoryService.sendBulkStepOneEmail(ids).subscribe({
          next: (r) => {
            this.sendingBulkInfo = false;
            this.messageService.add({ severity: 'success', summary: 'Emails envoyés', detail: `${r.sent_count} email(s) envoyé(s).`, life: 8000 });
          },
          error: (err) => { this.sendingBulkInfo = false; this.messageService.add({ severity: 'error', summary: 'Erreur', detail: err?.error?.detail || 'Envoi impossible.' }); },
        });
      },
    });
  }

  sendBulkSavingsEmail(): void {
    const ids = this.bulkProjectIds();
    if (!ids.length) { this.messageService.add({ severity: 'warn', summary: 'Sélection vide', detail: 'Aucune transaction sélectionnée.' }); return; }
    this.confirmationService.confirm({
      message: `Envoyer l'email d'économies pour ${ids.length} transaction(s) ?`,
      header: 'Confirmer l\'envoi groupé', icon: 'pi pi-percentage',
      acceptLabel: 'Envoyer', rejectLabel: 'Annuler',
      accept: () => {
        this.sendingBulkSavings = true;
        this.projectHistoryService.sendBulkStepOneSavingsEmail(ids).subscribe({
          next: (r) => {
            this.sendingBulkSavings = false;
            this.messageService.add({ severity: 'success', summary: 'Emails envoyés', detail: `${r.sent_count} email(s) envoyé(s).`, life: 8000 });
          },
          error: (err) => { this.sendingBulkSavings = false; this.messageService.add({ severity: 'error', summary: 'Erreur', detail: err?.error?.detail || 'Envoi impossible.' }); },
        });
      },
    });
  }

  // ===== SMS rappel devis groupé (Ringover) =====

  private transactionPhoneE164(t: Transaction): string | null {
    const raw = (t.contact?.mobile || '').trim();
    if (!raw) return null;
    const d = raw.replace(/\D/g, '');
    if (!d || d.length < 8) return null;
    if (d.startsWith('00')) return `+${d.slice(2)}`;
    if (d.startsWith('33')) return `+${d}`;
    if (d.startsWith('0'))  return `+33${d.slice(1)}`;
    return `+${d}`;
  }

  private buildSignature(agent: UserProfile): string {
    const lines: string[] = [];
    if (agent.username) lines.push(agent.username.trim());
    if (agent.telephone_fixe) lines.push(agent.telephone_fixe.trim());
    if (agent.telephone) lines.push(agent.telephone.trim());
    if (agent.email) lines.push(agent.email.trim());
    lines.push('Cabinet BROKINS'); lines.push('www.brokins.fr');
    return lines.join('\n');
  }

  private formatCiviliteForSms(civilite: string | undefined): string {
    const raw = (civilite || '').trim();
    const normalized = raw.toLowerCase().replace(/\./g, '');
    if (['mme', 'madame', 'mlle', 'mademoiselle'].includes(normalized)) return 'Mme';
    if (['m', 'mr', 'monsieur'].includes(normalized)) return 'M.';
    return raw;
  }

  sendBulkSmsRappel(): void {
    const withPhone = this.selected.filter(t => this.transactionPhoneE164(t));
    const without = this.selected.length - withPhone.length;
    if (!withPhone.length) { this.messageService.add({ severity: 'warn', summary: 'Aucun numéro', detail: 'Aucune transaction sélectionnée n\'a de mobile.' }); return; }
    this.confirmationService.confirm({
      message: `Envoyer un SMS "Rappel Devis" à ${withPhone.length} contact(s) ?` + (without > 0 ? `\n(${without} ignoré(s) — sans mobile)` : ''),
      header: 'Confirmer l\'envoi SMS', icon: 'pi pi-comment',
      acceptLabel: 'Envoyer', rejectLabel: 'Annuler',
      accept: () => {
        // Un seul appel : le serveur sérialise et cadence les envois, puis
        // bascule sur WhatsApp les destinataires que Ringover a refusés.
        // L'ancienne rafale d'appels parallèles saturait l'API dès les
        // premiers contacts (429), d'où des lots partiellement envoyés.
        const contactIds = withPhone
          .map(t => t.contact?.id ?? t.contact_id)
          .filter((id): id is number => id != null);
        if (!contactIds.length) {
          this.messageService.add({
            severity: 'warn', summary: 'Contacts introuvables',
            detail: "Aucune transaction sélectionnée n'est rattachée à un contact.",
          });
          return;
        }
        this.sendingBulkSms = true;
        this.ringoverService.sendSmsBulk({
          contact_ids: contactIds,
          template: 'relance_simulations',
        }).subscribe({
          next: (res) => this.onBulkSmsDone(res),
          error: () => {
            this.sendingBulkSms = false;
            this.messageService.add({
              severity: 'error', summary: 'Échec envoi',
              detail: "L'envoi en masse n'a pas pu être lancé.", life: 8000,
            });
          },
        });
      },
    });
  }

  private onBulkSmsDone(res: BulkSmsResponse): void {
    this.sendingBulkSms = false;
    const sent = res.sent_sms + res.sent_whatsapp;
    // Le détail par canal compte : WhatsApp est facturé au message, une
    // proportion inhabituelle signale que le SMS décroche.
    const parts: string[] = [];
    if (res.sent_sms) parts.push(`${res.sent_sms} par SMS`);
    if (res.sent_whatsapp) parts.push(`${res.sent_whatsapp} par WhatsApp (SMS en échec)`);
    if (res.failed) parts.push(`${res.failed} échec(s)`);
    this.messageService.add({
      severity: sent > 0 ? (res.failed ? 'warn' : 'success') : 'error',
      summary: sent > 0 ? 'Messages envoyés' : 'Échec envoi',
      detail: sent > 0
        ? `${sent}/${res.total} message(s) envoyé(s) — ${parts.join(', ')}.`
        : `Aucun message envoyé (${res.failed} erreur(s)).`,
      life: 10000,
    });
  }

  // ===== Attribution groupée (admin) =====

  assignBulk(): void {
    if (!this.isAdmin) return;
    if (!this.selectedAssignUserId) { this.messageService.add({ severity: 'warn', summary: 'Conseiller', detail: 'Choisissez un conseiller.' }); return; }
    if (!this.selected.length) { this.messageService.add({ severity: 'warn', summary: 'Sélection vide', detail: 'Aucune transaction sélectionnée.' }); return; }
    const target = this.selectedAssignUserId;
    const label = this.usersFilter.find(u => u.value === target)?.label || 'le conseiller';
    this.confirmationService.confirm({
      message: `Attribuer ${this.selected.length} transaction(s) à ${label} ?`,
      header: 'Confirmer l\'attribution', icon: 'pi pi-users',
      acceptLabel: 'Confirmer', rejectLabel: 'Annuler',
      accept: () => {
        this.assigningBulk = true;
        forkJoin(this.selected.map(t => this.transactionService.update(t.id, { user_id: target } as any))).subscribe({
          next: () => {
            this.assigningBulk = false;
            this.messageService.add({ severity: 'success', summary: 'Attribuées', detail: `${this.selected.length} transaction(s) attribuée(s) à ${label}.` });
            this.selectedAction = null; this.selectedAssignUserId = null; this.selected = [];
            this.load();
          },
          error: (err) => { this.assigningBulk = false; this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Attribution impossible.' }); console.error(err); },
        });
      },
    });
  }

  isBulkBusy(): boolean {
    return this.exportingExcel || this.sendingBulkInfo || this.sendingBulkSavings
      || this.sendingBulkSms || this.assigningBulk || this.importingCsv || this.deletingByExcel;
  }

  // ===== Import CSV OGGO (admin) — réutilise ProjectHistory =====

  triggerCsvImport(): void {
    if (!this.isAdmin || this.importingCsv) return;
    this.csvImportInput?.nativeElement.click();
  }

  onCsvFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.csv') && !lower.endsWith('.txt')) {
      this.messageService.add({ severity: 'warn', summary: 'Format invalide', detail: 'Sélectionnez un fichier CSV.' });
      input.value = ''; return;
    }
    this.importingCsv = true;
    this.projectHistoryService.importProjectsCsv(file).subscribe({
      next: (r) => {
        const doublonInfo = r.doublons > 0 ? `, ${r.doublons} doublon(s)` : '';
        this.messageService.add({
          severity: r.skipped > 0 ? 'warn' : 'success',
          summary: 'Import terminé',
          detail: `${r.imported} importé(s), ${r.skipped} ignoré(s)${doublonInfo}.`,
          life: 10000,
        });
        this.importingCsv = false;
        input.value = '';
        this.load();
      },
      error: (err) => {
        this.importingCsv = false;
        input.value = '';
        this.messageService.add({ severity: 'error', summary: 'Import échoué', detail: err?.error?.detail || 'Impossible d\'importer le CSV.' });
      },
    });
  }

  // ===== Suppression par Excel mobile (admin) — réutilise ProjectHistory =====

  triggerMobileDeleteExcel(): void {
    if (!this.isAdmin || this.deletingByExcel) return;
    this.mobileDeleteExcelInput?.nativeElement.click();
  }

  onMobileDeleteExcelSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.xlsm')) {
      this.messageService.add({ severity: 'warn', summary: 'Format invalide', detail: 'Sélectionnez un fichier Excel .xlsx.' });
      input.value = ''; return;
    }
    this.confirmationService.confirm({
      message: `Importer "${file.name}" pour supprimer/vider les numéros listés ?`,
      header: 'Confirmer la suppression par Excel', icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Exécuter', rejectLabel: 'Annuler', acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deletingByExcel = true;
        this.projectHistoryService.deleteProjectsByMobileExcel(file).subscribe({
          next: (r) => {
            this.deletingByExcel = false;
            input.value = '';
            this.messageService.add({ severity: 'success', summary: 'Traitement terminé', detail: `${r.deleted_count} supprimé(s).`, life: 10000 });
            this.load();
          },
          error: (err) => {
            this.deletingByExcel = false;
            input.value = '';
            this.messageService.add({ severity: 'error', summary: 'Traitement échoué', detail: err?.error?.detail || 'Impossible de traiter le fichier.' });
          },
        });
      },
      reject: () => { input.value = ''; },
    });
  }

  // ===== helpers =====

  parseDevisHistory(t: Transaction): any[] {
    if (!t.nom_assureurs) return [];
    try {
      const parsed = JSON.parse(t.nom_assureurs);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    } catch {}
    return [];
  }

  banqueLabel(code?: string): string {
    if (!code) return '';
    return BANQUE_MAP.get(code) || code;
  }

  contactName(t: Transaction): string {
    const c = t.contact;
    if (!c) return '—';
    return [c.prenom, c.nom].filter(Boolean).join(' ') || c.reference || `#${c.id}`;
  }

  contactInitials(t: Transaction): string {
    const c = t.contact;
    if (!c) return '?';
    const f = (c.prenom || '').charAt(0);
    const l = (c.nom || '').charAt(0);
    return (f + l).toUpperCase() || '?';
  }

  contactAvatarColor(t: Transaction): string {
    const id = t.contact?.id ?? t.contact_id ?? 0;
    return AVATAR_COLORS[id % AVATAR_COLORS.length];
  }

  hasDevis(t: Transaction): boolean {
    return t.devis_envoye === true || Number(t.nbre_devis_envoyes) > 0;
  }

  phaseSeverity(phase?: string): any {
    switch (phase) {
      case 'Fermé gagné':                   return 'success';
      case 'Fermé perdu':                   return 'danger';
      case 'Devis envoyé':
      case 'Questionnaire médical envoyé':  return 'info';
      case 'Relance & objections en cours': return 'warning';
      default:                              return 'secondary';
    }
  }
}
